import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  APP_BUILD_ID,
  clearPayFastCheckout,
  orderedPayFastFieldEntries,
  readPayFastCheckout,
} from '../utils/payfastCheckout';
import { verifyPayFastSignature } from '../utils/payfastSignature';

function isStalePayFastCheckout(buildId?: string): string | null {
  if (buildId !== APP_BUILD_ID) {
    return `App build mismatch (have ${buildId || 'none'}, need ${APP_BUILD_ID}). Close all tabs, hard-refresh (Ctrl+Shift+R), then try again.`;
  }
  return null;
}

const PayFastCheckout: React.FC = () => {
  const submitRef = useRef<HTMLButtonElement>(null);
  const [checkout] = useState(() => readPayFastCheckout());
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(3);

  const fieldEntries = useMemo(
    () => (checkout ? orderedPayFastFieldEntries(checkout.fields) : []),
    [checkout]
  );

  useEffect(() => {
    if (!checkout) {
      setError('No payment session found. Please try registering again.');
      return;
    }

    clearPayFastCheckout();

    const staleReason = isStalePayFastCheckout(checkout.buildId);
    if (staleReason) {
      console.error('PayFast checkout blocked:', staleReason);
      setError(staleReason);
      return;
    }

    const signatureOk = verifyPayFastSignature(checkout.fields, checkout.passphrase);
    console.log('PayFast checkout redirect — build:', APP_BUILD_ID);
    console.log('PayFast checkout redirect — signature self-check:', signatureOk);
    console.log('PayFast checkout fields:', JSON.stringify(checkout.fields, null, 2));

    if (!signatureOk) {
      setError('Payment signature could not be verified. Please try again.');
      return;
    }

    const countdownTimer = window.setInterval(() => {
      setCountdown((value) => {
        if (value <= 1) {
          window.clearInterval(countdownTimer);
          submitRef.current?.click();
          return 0;
        }
        return value - 1;
      });
    }, 1000);

    return () => window.clearInterval(countdownTimer);
  }, [checkout]);

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <h1 className="text-xl font-bold mb-4 text-red-600">Payment Error</h1>
          <p className="text-gray-600 mb-2">{error}</p>
          <p className="text-xs text-gray-400 mb-6">Required build: {APP_BUILD_ID}</p>
          <Link to="/events" className="text-primary hover:underline">
            Back to events
          </Link>
        </div>
      </div>
    );
  }

  if (!checkout) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
        <p className="text-gray-600">Preparing PayFast checkout...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
        <h1 className="text-xl font-bold mb-4">Redirecting to PayFast</h1>
        <p className="text-xs text-gray-400 mb-2">Build {APP_BUILD_ID}</p>
        <p className="text-gray-600 mb-4">
          {countdown > 0
            ? `Connecting in ${countdown} second${countdown === 1 ? '' : 's'}...`
            : 'Opening PayFast...'}
        </p>
        <div className="text-left text-xs text-gray-500 mb-6 space-y-1 break-all">
          <div>
            <span className="font-semibold">Return URL:</span> {checkout.fields.return_url}
          </div>
          <div>
            <span className="font-semibold">Amount:</span> R{checkout.fields.amount}
          </div>
        </div>
        {/* Same-tab submit so PayFast's return redirect comes back to this session */}
        <form method="POST" action={checkout.action}>
          {fieldEntries.map(([key, value]) => (
            <input key={key} type="hidden" name={key} value={value} />
          ))}
          <button
            ref={submitRef}
            type="submit"
            className="bg-primary text-white px-6 py-3 rounded-lg font-semibold w-full"
          >
            Continue to PayFast
          </button>
        </form>
      </div>
    </div>
  );
};

export default PayFastCheckout;
